import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, getHeaders } from "../utils/api";
import { readAdminApiErrorMessage } from "../utils/adminUi";
import SearchableDropdown from "./shared/SearchableDropdown";
import { showToast } from "../utils/toast";
import { trackEvent } from "../services/applicationInsights";
import {
  FaKey,
  FaFilter,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaSyncAlt,
  FaShieldAlt,
  FaInfoCircle,
  FaCopy
} from "react-icons/fa";

const normalizeHostname = (value) => {
  const rawValue = Array.isArray(value) ? value.find(Boolean) : value;
  const rawHostname = (rawValue ?? "").toString().trim();

  if (!rawHostname) return "";

  try {
    const url = /^https?:\/\//i.test(rawHostname) ? rawHostname : `https://${rawHostname}`;
    return new URL(url).hostname || rawHostname;
  } catch {
    return rawHostname.replace(/^https?:\/\//i, "").split(/[/?#]/)[0];
  }
};

const getHostnameFromSource = (source) => {
  if (!source || typeof source !== "object") return "";

  return normalizeHostname(
    source.hostname ||
      source.hostName ||
      source.HostName ||
      source.domainName ||
      source.DomainName ||
      source.domainNames ||
      source.DomainNames ||
      source.domains ||
      source.Domains ||
      ""
  );
};

/* ------------------ Main Component ------------------ */
const ApiKeys = ({ token, orgId, roles = [] }) => {
  const roleList = Array.isArray(roles) ? roles : [];
  const isSystemAdmin = useMemo(() => {
    return roleList.some((role) => String(role).toLowerCase() === "systemadministrator");
  }, [roleList]);

  const fetchSitesAbortRef = useRef(null);
  const fetchKeysAbortRef = useRef(null);
  const autoSyncedKeysRef = useRef("");

  const [organizations, setOrganizations] = useState([]);
  const [siteCollections, setSiteCollections] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => {
    return isSystemAdmin ? "" : (orgId || "");
  });
  const [selectedSite, setSelectedSite] = useState("");

  const [fetchingOrgs, setFetchingOrgs] = useState(false);
  const [fetchingSites, setFetchingSites] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Modal state (Generate confirmation)
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Modal state (Revoke confirmation)
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  // Modal state (one-time plaintext key reveal after successful generate)
  const [revealedKey, setRevealedKey] = useState(null); // { id, apiKey, createdAt }
  const [revealCopied, setRevealCopied] = useState(false);

  // List state
  const [apiKeys, setApiKeys] = useState([]);
  const [fetchingKeys, setFetchingKeys] = useState(false);

  useEffect(() => {
    return () => {
      fetchSitesAbortRef.current?.abort();
      fetchKeysAbortRef.current?.abort();
    };
  }, []);

  const parseOrganizationsError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view organizations.",
      500: "An error occurred while loading organizations.",
      default: "Failed to load organizations"
    });
  }, []);

  const parseSiteCollectionsError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: "Invalid site collection request context.",
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view site collections.",
      404: "No site collections were found for the selected organization.",
      500: "An error occurred while loading site collections.",
      default: "Failed to load site collections"
    });
  }, []);

  const parseApiKeyListError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: "Invalid API key lookup request.",
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to view API keys for this site.",
      404: "API key not found for the specified organization and site.",
      500: "An error occurred while retrieving API keys.",
      default: "Failed to fetch API keys"
    });
  }, []);

  const parseGenerateApiKeyError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: "Invalid request to generate an API key.",
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to generate API keys for this site.",
      409: "Maximum allowed API keys reached for this site collection. Revoke an existing key before generating a new one.",
      500: "An unexpected server error occurred while generating API key.",
      default: "Failed to generate API key"
    });
  }, []);

  const parseRevokeApiKeyError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: "Your session is invalid or expired. Please sign in again.",
      403: "You do not have permission to revoke API keys for this site.",
      404: "API key not found.",
      500: "An unexpected server error occurred while revoking API key.",
      default: "Failed to revoke API key"
    });
  }, []);

  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrg !== orgId) {
      setSelectedOrg(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrg]);

  useEffect(() => {
    if (!isSystemAdmin) return;

    setFetchingOrgs(true);
    fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders(token) })
      .then(async (res) => {
        if (!res.ok) {
          const message = await parseOrganizationsError(res);
          throw new Error(message);
        }

        return res.json().catch(() => []);
      })
      .then((data) => {
        const organizationsList = Array.isArray(data) ? data : [];
        setOrganizations(organizationsList);
        setSelectedOrg((prev) => {
          if (prev && !organizationsList.some((o) => o.id === prev)) {
            return "";
          }
          return prev;
        });
      })
      .catch((err) => showToast.error(err.message || "Failed to load organizations"))
      .finally(() => setFetchingOrgs(false));
  }, [isSystemAdmin, parseOrganizationsError, token]);

  useEffect(() => {
    const targetOrgId = isSystemAdmin ? selectedOrg : selectedOrg || orgId;
    if (!targetOrgId) {
      fetchSitesAbortRef.current?.abort();
      setSiteCollections([]);
      setSelectedSite("");
      setFetchingSites(false);
      return;
    }

    fetchSitesAbortRef.current?.abort();
    const controller = new AbortController();
    fetchSitesAbortRef.current = controller;

    setFetchingSites(true);
    fetch(`${API_BASE_URL}/site-collections?organizationId=${targetOrgId}`, {
      headers: getHeaders(token),
      signal: controller.signal
    })
      .then(async (res) => {
        if (!res.ok) {
          const message = await parseSiteCollectionsError(res);
          throw new Error(message);
        }

        return res.json().catch(() => []);
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setSiteCollections(list);
        setSelectedSite((prev) => {
          if (prev && !list.some((s) => s.id === prev)) {
            return "";
          }
          return prev;
        });
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        showToast.error(err.message || "Failed to load site collections");
        setSiteCollections([]);
        setSelectedSite("");
      })
      .finally(() => {
        if (fetchSitesAbortRef.current === controller) {
          setFetchingSites(false);
        }
      });
  }, [isSystemAdmin, orgId, parseSiteCollectionsError, selectedOrg, token]);

  const effectiveOrgId = isSystemAdmin ? selectedOrg : selectedOrg || orgId;
  const selectedSiteCollection = useMemo(
    () => siteCollections.find((site) => String(site?.id) === String(selectedSite)),
    [selectedSite, siteCollections]
  );
  const hostname = useMemo(() => getHostnameFromSource(selectedSiteCollection), [selectedSiteCollection]);

  const formatCreatedAt = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    } catch {
      return String(value);
    }
  };

  // Backend may serialize the masked key under several names; accept common variants.
  const getMaskedApiKey = (item) => {
    if (!item || typeof item !== "object") return "";
    return (
      item.apiKey ||
      item.maskedApiKey ||
      item.apiKeyMasked ||
      item.maskedKey ||
      item.keyMasked ||
      item.key ||
      ""
    );
  };

  const fetchApiKeys = useCallback(async () => {
    if (!effectiveOrgId || !selectedSite) return;

    fetchKeysAbortRef.current?.abort();
    const controller = new AbortController();
    fetchKeysAbortRef.current = controller;

    setFetchingKeys(true);

    try {
      const qs = hostname ? `?hostname=${encodeURIComponent(hostname)}` : "";
      const res = await fetch(        
        `${API_BASE_URL}/apikeys/organizations/${effectiveOrgId}/site-collections/${selectedSite}${qs}`,
        {
          headers: getHeaders(token),
          signal: controller.signal
        }
      );

      if (res.status === 404) {
        // "not found" is treated as a valid empty state in API-key list retrieval.
        setApiKeys([]);
        return;
      }

      if (!res.ok) {
        const message = await parseApiKeyListError(res);
        throw new Error(message);
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : data ? [data] : [];
      setApiKeys(list);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setApiKeys([]);
      showToast.error(e.message);
    } finally {
      if (fetchKeysAbortRef.current === controller) {
        setFetchingKeys(false);
      }
    }
  }, [effectiveOrgId, hostname, parseApiKeyListError, selectedSite, token]);

  useEffect(() => {
    if (!effectiveOrgId || !selectedSite) {
      autoSyncedKeysRef.current = "";
      fetchKeysAbortRef.current?.abort();
      setApiKeys([]);
      return;
    }

    const syncKey = `${effectiveOrgId}:${selectedSite}`;
    if (autoSyncedKeysRef.current === syncKey) {
      return;
    }

    autoSyncedKeysRef.current = syncKey;
    fetchApiKeys();
  }, [effectiveOrgId, fetchApiKeys, selectedSite]);

  const handleGenerateClick = () => {
    if (!effectiveOrgId || !selectedSite) {
      showToast.warning("Please select both Organization and Site Collection");
      return;
    }
    setShowGenerateModal(true);
  };

  const handleGenerateConfirm = async () => {
    if (!effectiveOrgId || !selectedSite) return;

    setSubmitting(true);
    setShowGenerateModal(false);

    try {      
      const res = await fetch(`${API_BASE_URL}/apikeys/organizations/${effectiveOrgId}/site-collections/${selectedSite}/generate`, {
        method: "POST",
        headers: getHeaders(token)
      });

      if (!res.ok) {
        if (res.status === 409) {
          throw new Error(
            "Maximum allowed API keys reached for this site collection. Revoke an existing key before generating a new one."
          );
        }
        const message = await parseGenerateApiKeyError(res);
        throw new Error(message);
      }

      const created = await res.json().catch(() => null);
      const plaintext =
        (created && (created.apiKey || created.key || created.plainKey || created.secret)) || "";
      if (created && plaintext) {
        setRevealedKey({
          id: created.id,
          apiKey: plaintext,
          createdAt: created.createdAt
        });
        setRevealCopied(false);
      }

      showToast.success("API key generated successfully.");
      await fetchApiKeys();
    } catch (e) {
      showToast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeClick = (keyItem) => {
    if (!keyItem?.id) {
      showToast.error("Cannot revoke: key identifier is missing.");
      return;
    }

    setRevokeTarget(keyItem);
    setShowRevokeModal(true);
  };

  const handleRevokeConfirm = async () => {
    const keyId = revokeTarget?.id;

    if (!effectiveOrgId || !selectedSite || !keyId) {
      showToast.error("Cannot revoke: key identifier is missing.");
      return;
    }

    setSubmitting(true);
    setShowRevokeModal(false);

    try {      
      const res = await fetch(`${API_BASE_URL}/apikeys/organizations/${effectiveOrgId}/site-collections/${selectedSite}/${encodeURIComponent(keyId)}`, {
        method: "DELETE",
        headers: getHeaders(token)
      });

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error("API key not found.");
        }
        const message = await parseRevokeApiKeyError(res);
        throw new Error(message);
      }

      showToast.success("API key revoked successfully.");
      setRevokeTarget(null);

      await fetchApiKeys();
    } catch (e) {
      showToast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (value) => {
    if (!value) return false;

    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return true;
      } catch {
        return false;
      }
    }
  };

  const copyRevealedKey = async () => {
    const ok = await copyToClipboard(revealedKey?.apiKey);
    if (ok) {
      setRevealCopied(true);
      showToast.success("API key copied to clipboard.");
    } else {
      showToast.error("Copy failed. Please copy manually.");
    }
  };

  const closeRevealModal = () => {
    setRevealedKey(null);
    setRevealCopied(false);
  };

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}>
              <FaKey size={22} />
            </div>
            <div>
              <h1 style={titleStyle}>API Keys Management</h1>
              <p style={subtitleStyle}>Generate and manage API keys for secure access.</p>
            </div>
          </div>
        </header>

        <div style={filtersCardStyle}>
          <div style={cardHeaderStyle}>
            <FaFilter color="#6366f1" size={14} />
            <span style={cardTitleStyle}>Selection Criteria</span>
          </div>

          <div style={isSystemAdmin ? filtersGridStyleTwoColumn : filtersGridStyleSingleColumn}>
            {isSystemAdmin && (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(val) => {
                    setSelectedOrg(val);
                    setSelectedSite("");
                    if (val && selectedSite) {
                      trackEvent('Dropdown Selected', {
                        section: 'apiKeys',
                        organizationId: val,
                        siteCollectionId: selectedSite
                      });
                    }
                  }}
                  placeholder="Select Organization"
                  isLoading={fetchingOrgs}
                  disabled={fetchingOrgs}
                  styles={searchableDropdownStyles}
                />
              </div>
            )}

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Site Collection</label>
              <SearchableDropdown
                options={siteCollections}
                selectedValue={selectedSite}
                onSelect={(val) => {
                  setSelectedSite(val);
                  if (val && effectiveOrgId) {
                    trackEvent('Dropdown Selected', {
                      section: 'apiKeys',
                      organizationId: effectiveOrgId,
                      siteCollectionId: val
                    });
                  }
                }}
                placeholder={!effectiveOrgId && isSystemAdmin ? "Select Org First" : "Select Site Collection"}
                isLoading={fetchingSites}
                disabled={fetchingSites || (isSystemAdmin && !selectedOrg)}
                styles={searchableDropdownStyles}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={actionCardStyle}>
          <div style={cardHeaderStyle}>
            <FaShieldAlt color="#6366f1" size={14} />
            <span style={cardTitleStyle}>API Key Actions</span>
          </div>
          <div style={actionButtonsRow}>
            <button
              onClick={handleGenerateClick}
              disabled={submitting || !effectiveOrgId || !selectedSite}
              style={{
                ...primaryButtonStyle,
                opacity: submitting || !effectiveOrgId || !selectedSite ? 0.6 : 1
              }}
            >
              <FaSyncAlt className={submitting ? "spin" : ""} /> {submitting ? "Processing..." : "Generate API Key"}
            </button>

            <button
              onClick={() => fetchApiKeys()}
              disabled={submitting || !effectiveOrgId || !selectedSite}
              style={{
                ...secondaryButtonStyle,
                opacity: submitting || !effectiveOrgId || !selectedSite ? 0.6 : 1
              }}
            >
              <FaSyncAlt /> Refresh Existing API keys
            </button>
          </div>
        </div>

        {/* List */}
        <div style={existingKeyCardStyle}>
          <div style={{ ...cardHeaderStyle, justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FaKey color="#6366f1" size={14} />
              <span style={cardTitleStyle}>API Keys</span>
            </div>

            {fetchingKeys && (
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                <FaSyncAlt className="spin" /> Loading...
              </div>
            )}
          </div>

          {fetchingKeys ? (
            <div
              style={{
                padding: "12px 4px",
                color: "#64748b",
                fontWeight: 600,
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
            >
              <FaSyncAlt className="spin" /> Loading API keys...
            </div>
          ) : apiKeys.length === 0 ? (
            <div style={{ padding: "12px 4px", color: "#64748b", fontWeight: 600, fontSize: 14 }}>
              {isSystemAdmin && !selectedOrg
                ? "Select an organization to begin."
                : !selectedSite
                  ? "Select a site collection to begin."
                  : "No API keys found for this site collection."}
            </div>
          ) : (
            <div style={tableStyle}>
              <div style={{ ...tableRowStyle, ...tableHeaderRowStyle }}>
                <div style={tableCellStyle}>Key ID</div>
                <div style={tableCellStyle}>API Key</div>
                <div style={tableCellStyle}>Created</div>
                <div style={tableCellStyle}>Status</div>
                <div style={{ ...tableCellStyle, textAlign: "right" }}>Action</div>
              </div>

              {apiKeys.map((k) => {
                const isActive = !k.isRevoked;

                return (
                  <div key={k.id} style={tableRowStyle}>
                    <div
                      style={{
                        ...tableCellStyle,
                        fontFamily: "monospace",
                        wordBreak: "break-all"
                      }}
                      title={k.id || ""}
                    >
                      {k.id || ""}
                    </div>

                    <div
                      style={{
                        ...tableCellStyle,
                        fontFamily: "monospace",
                        wordBreak: "break-all"
                      }}
                    >
                      {getMaskedApiKey(k)}
                    </div>

                    <div style={tableCellStyle}>{formatCreatedAt(k.createdAt)}</div>

                    <div style={tableCellStyle}>
                      <span
                        style={{
                          ...statusBadgeStyle,
                          background: isActive ? "#d1fae5" : "#fee2e2",
                          color: isActive ? "#065f46" : "#991b1b"
                        }}
                      >
                        {isActive ? "Active" : "Revoked"}
                      </span>
                    </div>

                    <div style={{ ...tableCellStyle, textAlign: "right" }}>
                      <button
                        type="button"
                        onClick={() => handleRevokeClick(k)}
                        disabled={submitting || !isActive}
                        style={{
                          ...dangerButtonStyle,
                          opacity: submitting || !isActive ? 0.6 : 1,
                          cursor: submitting || !isActive ? "not-allowed" : "pointer"
                        }}
                        title={!isActive ? "Already revoked" : "Revoke this key"}
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Generate Confirmation Modal */}
        {showGenerateModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaKey color="#6366f1" size={20} />
                <h3 style={modalTitleStyle}>Generate API Key</h3>
              </div>

              <div style={modalBodyStyle}>
                <div style={infoBoxStyle}>
                  <FaInfoCircle color="#6366f1" />
                  <span style={infoTextStyle}>
                    A new API key will be created for this site collection. The full key value will be shown
                    <strong> only once</strong> after creation &mdash; copy it immediately.
                  </span>
                </div>
              </div>

              <div style={modalFooterStyle}>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  style={modalCancelButtonStyle}
                >
                  Cancel
                </button>

                <button
                  onClick={handleGenerateConfirm}
                  style={modalConfirmButtonStyle}
                >
                  <FaCheck /> Generate Key
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Confirmation Modal */}
        {showRevokeModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaExclamationTriangle color="#b91c1c" size={20} />
                <h3 style={modalTitleStyle}>Confirm Revoke</h3>
              </div>

              <div style={modalBodyStyle}>
                <div style={{ ...infoBoxStyle, background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
                  <FaInfoCircle color="#991b1b" />
                  <span style={infoTextStyle}>
                    This action cannot be undone. Any client using this key will lose runtime access.
                  </span>
                </div>

                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>Key ID</label>
                  <div style={{ ...modalInputStyle, fontFamily: "monospace", background: "#f8fafc", wordBreak: "break-all" }}>
                    {revokeTarget?.id || ""}
                  </div>
                </div>

                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>API Key (masked)</label>
                  <div style={{ ...modalInputStyle, fontFamily: "monospace", background: "#f8fafc", wordBreak: "break-all" }}>
                    {getMaskedApiKey(revokeTarget)}
                  </div>
                </div>
              </div>

              <div style={modalFooterStyle}>
                <button
                  onClick={() => {
                    setShowRevokeModal(false);
                    setRevokeTarget(null);
                  }}
                  style={modalCancelButtonStyle}
                >
                  Cancel
                </button>

                <button
                  onClick={handleRevokeConfirm}
                  disabled={!revokeTarget?.id}
                  style={{
                    ...modalConfirmButtonStyle,
                    background: "#b91c1c",
                    opacity: revokeTarget?.id ? 1 : 0.6,
                    cursor: revokeTarget?.id ? "pointer" : "not-allowed"
                  }}
                >
                  <FaTimes /> Revoke Key
                </button>
              </div>
            </div>
          </div>
        )}

        {/* One-time Plaintext Key Reveal Modal */}
        {revealedKey && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={modalHeaderStyle}>
                <FaKey color="#6366f1" size={20} />
                <h3 style={modalTitleStyle}>API Key Created</h3>
              </div>

              <div style={modalBodyStyle}>
                <div style={{ ...infoBoxStyle, background: "#fffbeb", borderColor: "#fde68a", color: "#92400e" }}>
                  <FaExclamationTriangle color="#b45309" />
                  <span style={infoTextStyle}>
                    This key will <strong>not be shown again</strong>. Copy it before closing.
                  </span>
                </div>

                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>Key ID</label>
                  <div style={{ ...modalInputStyle, fontFamily: "monospace", background: "#f8fafc", wordBreak: "break-all" }}>
                    {revealedKey.id || ""}
                  </div>
                </div>

                <div style={inputGroupStyle}>
                  <label style={modalLabelStyle}>API Key</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "stretch",
                      gap: 8
                    }}
                  >
                    <div
                      style={{
                        ...modalInputStyle,
                        flex: 1,
                        fontFamily: "monospace",
                        background: "#f8fafc",
                        wordBreak: "break-all"
                      }}
                    >
                      {revealedKey.apiKey}
                    </div>
                    <button
                      type="button"
                      onClick={copyRevealedKey}
                      style={{
                        ...modalConfirmButtonStyle,
                        background: revealCopied ? "#16a34a" : "#6366f1"
                      }}
                      aria-label={revealCopied ? "API key copied" : "Copy API key to clipboard"}
                    >
                      {revealCopied ? <FaCheck /> : <FaCopy />} {revealCopied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>

              <div style={modalFooterStyle}>
                <button
                  onClick={closeRevealModal}
                  style={modalCancelButtonStyle}
                >
                  {revealCopied ? "I've copied the key — close" : "Close"}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .spin { animation: fa-spin 2s infinite linear; } 
          @keyframes fa-spin { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
        `}</style>
      </div>
    </div>
  );
};

export default ApiKeys;

/* ------------------ STYLES ------------------ */
const dropdownContainerStyle = { position: "relative", width: "100%" };
const dropdownTriggerStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  border: "1.5px solid #e2e8f0",
  fontSize: 14,
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  fontWeight: 500,
  color: "#1e293b",
  transition: "all 0.2s ease",
  outline: "none"
};
const dropdownTriggerTextStyle = { flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const dropdownMenuStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "#fff",
  border: "1.5px solid #e2e8f0",
  borderRadius: 10,
  marginTop: 4,
  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  overflow: "hidden"
};
const searchBoxContainerStyle = { position: "relative", padding: "8px 12px", borderBottom: "1px solid #f1f5f9", background: "#fcfdfe" };
const searchIconStyle = { position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12, pointerEvents: "none" };
const dropdownSearchInputStyle = {
  width: "100%",
  padding: "8px 12px 8px 28px",
  borderRadius: 6,
  border: "1px solid #e2e8f0",
  fontSize: 13,
  outline: "none",
  fontFamily: "inherit",
  background: "#fff"
};
const dropdownListStyle = { maxHeight: "200px", overflowY: "auto", padding: "4px" };
const dropdownOptionStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: 6,
  margin: "2px 0",
  transition: "all 0.2s ease",
  fontSize: 13
};
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: "inherit" };
const optionIdStyle = { fontSize: "11px", opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: 13 };
const searchableDropdownStyles = {
  container: dropdownContainerStyle,
  trigger: dropdownTriggerStyle,
  openTrigger: { border: "1.5px solid #6366f1" },
  triggerText: dropdownTriggerTextStyle,
  indicator: { color: "#94a3b8" },
  menu: dropdownMenuStyle,
  searchContainer: searchBoxContainerStyle,
  searchIcon: searchIconStyle,
  searchInput: dropdownSearchInputStyle,
  list: dropdownListStyle,
  option: dropdownOptionStyle,
  optionText: optionTextStyle,
  optionName: optionNameStyle,
  optionId: optionIdStyle,
  emptyOption: emptyOptionStyle
};

const wrapperStyle = { width: "100%", minHeight: "100vh", background: "#f8fafc", paddingBottom: 60, fontFamily: "Inter, sans-serif" };
const containerStyle = { maxWidth: 1200, margin: "0 auto", padding: "24px" };

const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: "flex", alignItems: "center", gap: 16 };
const headerBadgeStyle = {
  width: 48,
  height: 48,
  borderRadius: 12,
  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff"
};

const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: "#0f172a" };
const subtitleStyle = { fontSize: 14, color: "#64748b", margin: "4px 0 0 0" };

const filtersCardStyle = { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, border: "1px solid #e2e8f0" };
const cardHeaderStyle = { display: "flex", alignItems: "center", gap: 10, marginBottom: 20, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 };
const cardTitleStyle = { fontSize: 15, fontWeight: 700 };
const filtersGridStyleTwoColumn = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "end" };
const filtersGridStyleSingleColumn = { display: "grid", gridTemplateColumns: "1fr", gap: 16, maxWidth: 500 };
const inputGroupStyle = { display: "flex", flexDirection: "column", gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" };

const actionCardStyle = { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, border: "1px solid #e2e8f0" };
const actionButtonsRow = { display: "flex", gap: 16 };
const primaryButtonStyle = {
  background: "#6366f1",
  color: "#fff",
  border: "none",
  padding: "11px 20px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "all 0.2s ease"
};
const secondaryButtonStyle = {
  background: "#f1f5f9",
  color: "#64748b",
  border: "1px solid #e2e8f0",
  padding: "11px 20px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "all 0.2s ease"
};
const dangerButtonStyle = { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca", padding: "8px 12px", borderRadius: 10, fontWeight: 800 };


const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999
};
const modalContentStyle = { background: "#fff", borderRadius: 16, width: "90%", maxWidth: 520, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" };
const modalHeaderStyle = { display: "flex", alignItems: "center", gap: 12, padding: "24px 24px 16px 24px", borderBottom: "1px solid #f1f5f9" };
const modalTitleStyle = { fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 };
const modalBodyStyle = { padding: 24 };
const infoBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 16px",
  background: "#f5f3ff",
  border: "1px solid #e9d5ff",
  borderRadius: 8,
  marginBottom: 20,
  fontSize: 13,
  color: "#5b21b6"
};
const infoTextStyle = { flex: 1, fontWeight: 500 };
const modalLabelStyle = { fontSize: 13, fontWeight: 700, color: "#475569", marginBottom: 8 };
const modalInputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", fontFamily: "inherit" };
const modalFooterStyle = { display: "flex", justifyContent: "flex-end", gap: 12, padding: "16px 24px 24px 24px", borderTop: "1px solid #f1f5f9" };
const modalCancelButtonStyle = { padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 700 };
const modalConfirmButtonStyle = { padding: "10px 20px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", gap: 8 };

const existingKeyCardStyle = { background: "#fff", borderRadius: 16, padding: 24, marginBottom: 24, border: "1px solid #e2e8f0" };

const statusBadgeStyle = { padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 900, border: "1px solid rgba(0,0,0,0.06)" };

const tableStyle = { display: "flex", flexDirection: "column", gap: 6 };
const tableRowStyle = { display: "grid", gridTemplateColumns: "1.4fr 1.4fr 1fr 0.6fr 0.6fr", gap: 12, alignItems: "center", padding: "10px 6px", borderBottom: "1px solid #f1f5f9" };
const tableHeaderRowStyle = { fontSize: 12, fontWeight: 900, color: "#64748b", textTransform: "uppercase" };
const tableCellStyle = { fontSize: 13, fontWeight: 700, color: "#1e293b" };